import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useAuthoredRows,
  useCreateAuthoredRow,
  useUpdateAuthoredRow,
} from 'components/IAP/hooks/icsAuthoredFormHooks';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import SaveableField from '../SaveableField';
import {
  CG_COST_SECTIONS,
  CG_COST_TITLES,
  COST_DAY_COUNT,
  PREVIOUS_COST_FIELDS,
  type CgCostFormId,
  type CostDisplaySection,
} from '../cg5136Layout';
import { NewCgFormCard } from './newCgFormBoxes';

type CostInputs = {
  fpn?: string;
  projectName?: string;
  periodStart?: string;
  ceiling?: string;
  previous?: Record<string, string>;
  days?: Record<string, Record<string, Array<Record<string, string>>>>;
};

const DAY_OPTIONS = Array.from(
  { length: COST_DAY_COUNT },
  (_, index) => index + 1
);

function rowValues(
  inputs: CostInputs,
  day: number,
  section: CostDisplaySection,
  index: number
): Record<string, string> {
  return inputs.days?.[String(day)]?.[section.id]?.[index] ?? {};
}

/**
 * CG-5136 or CG-5136X. The card edits the cells a person types. Rates and
 * totals stay in the official workbook and are calculated on Excel export.
 */
const IcsCg5136Card = ({ formId }: { formId: CgCostFormId }) => {
  const incident = useIncident();
  const { effectivePeriodNumber } = useICSContext();
  const { data: workbooks = [] } = useAuthoredRows('cgCostWorkbooks');
  const createWorkbook = useCreateAuthoredRow('cgCostWorkbooks');
  const updateWorkbook = useUpdateAuthoredRow('cgCostWorkbooks');
  const recordVersionSave = useRecordFormVersionSave(
    formId,
    effectivePeriodNumber
  );
  const sections = CG_COST_SECTIONS[formId];
  const [day, setDay] = useState(1);
  const [sectionId, setSectionId] = useState(sections[0]?.id ?? 'personnel');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const workbook = workbooks.find((row) => row.form_base_id === formId) ?? null;
  const inputs = (workbook?.inputs as CostInputs | undefined) ?? {};
  const section =
    sections.find((entry) => entry.id === sectionId) ?? sections[0];

  const persist = (next: CostInputs) => {
    const onSuccess = () => recordVersionSave.mutate();
    if (!workbook) {
      createWorkbook.mutate(
        { form_base_id: formId, inputs: next },
        { onSuccess }
      );
      return;
    }
    updateWorkbook.mutate(
      { entityId: String(workbook.workbook_id), inputs: next },
      { onSuccess }
    );
  };

  const saveHeader = (patch: Partial<CostInputs>) =>
    persist({ ...inputs, ...patch });

  const savePrevious = (key: string, value: string) =>
    persist({ ...inputs, previous: { ...inputs.previous, [key]: value } });

  const beginEdit = (index: number) => {
    if (!section) return;
    setEditingIndex(index);
    setDraft(rowValues(inputs, day, section, index));
  };

  const commitEdit = (index: number) => {
    if (!section) return;
    const days = { ...inputs.days };
    const dayKey = String(day);
    const daySections = { ...(days[dayKey] ?? {}) };
    const rows = [...(daySections[section.id] ?? [])];
    while (rows.length < section.rowCount) rows.push({});
    rows[index] = draft;
    daySections[section.id] = rows;
    days[dayKey] = daySections;
    persist({ ...inputs, days });
    setEditingIndex(null);
  };

  return (
    <NewCgFormCard
      title={CG_COST_TITLES[formId]}
      formname={formId}
      baseId={formId}
      exportLabel="Export Excel"
    >
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        Rates and totals are calculated in the exported workbook. This card
        saves the cells you type.
      </p>

      <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
        <Ics204CardSection
          boxLabel="FPN"
          testId="cg5136-section-fpn"
          className="tw:mt-0"
        >
          <SaveableField
            id="cg5136-fpn"
            label="FPN"
            hideLabel
            value={inputs.fpn ?? ''}
            onSave={(value) => saveHeader({ fpn: value })}
          />
        </Ics204CardSection>
        <Ics204CardSection
          boxLabel="Project name"
          testId="cg5136-section-name"
          className="tw:mt-0"
        >
          <SaveableField
            id="cg5136-project-name"
            label="Project name"
            hideLabel
            value={inputs.projectName || incident.data?.name || ''}
            onSave={(value) => saveHeader({ projectName: value })}
          />
        </Ics204CardSection>
        <Ics204CardSection
          boxLabel="Reporting period start"
          testId="cg5136-section-period"
          className="tw:mt-0"
        >
          <SaveableField
            id="cg5136-period-start"
            label="Reporting period start"
            hideLabel
            type="date"
            value={inputs.periodStart ?? ''}
            onSave={(value) => saveHeader({ periodStart: value })}
          />
        </Ics204CardSection>
        <Ics204CardSection
          boxLabel="FPN ceiling"
          testId="cg5136-section-ceiling"
          className="tw:mt-0"
        >
          <SaveableField
            id="cg5136-ceiling"
            label="FPN ceiling"
            hideLabel
            value={inputs.ceiling ?? ''}
            onSave={(value) => saveHeader({ ceiling: value })}
          />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel="Previous period costs"
        testId="cg5136-section-previous"
        className="tw:mt-0"
      >
        <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:md:grid-cols-2">
          {PREVIOUS_COST_FIELDS.map((field) => (
            <SaveableField
              key={field.key}
              id={`cg5136-previous-${field.key}`}
              label={field.label}
              value={inputs.previous?.[field.key] ?? ''}
              onSave={(value) => savePrevious(field.key, value)}
            />
          ))}
        </div>
      </Ics204CardSection>

      <div className="tw:flex tw:flex-wrap tw:gap-2">
        <label className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
          Day
          <select
            aria-label="Cost workbook day"
            className="tw:h-8 tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-2"
            value={day}
            onChange={(event) => {
              setDay(Number(event.target.value));
              setEditingIndex(null);
            }}
          >
            {DAY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                Day {option}
              </option>
            ))}
          </select>
        </label>
        <label className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
          Section
          <select
            aria-label="Cost workbook section"
            className="tw:h-8 tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-2"
            value={section?.id ?? ''}
            onChange={(event) => {
              setSectionId(event.target.value);
              setEditingIndex(null);
            }}
          >
            {sections.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.title}
              </option>
            ))}
          </select>
        </label>
      </div>

      {section ? (
        <div className="tw:overflow-x-auto" data-testid="cg5136-day-table">
          <table className="tw:w-full tw:text-sm">
            <thead>
              <tr>
                {section.rowLabels ? (
                  <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                    Pay grade
                  </th>
                ) : null}
                {section.columns.map((column) => (
                  <th
                    key={column.key}
                    className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground"
                  >
                    {column.label}
                  </th>
                ))}
                <th className="tw:w-24" />
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: section.rowCount }, (_, index) => {
                const current = rowValues(inputs, day, section, index);
                const isEditing = editingIndex === index;
                const label = section.rowLabels?.[index] ?? `row ${index + 1}`;
                return (
                  <tr key={index}>
                    {section.rowLabels ? (
                      <td className="tw:px-2 tw:py-1 tw:align-top">{label}</td>
                    ) : null}
                    {section.columns.map((column) => {
                      const placeholder =
                        section.placeholders?.[column.key]?.[index];
                      const shown = current[column.key] || '';
                      return (
                        <td
                          key={column.key}
                          className="tw:px-1 tw:py-1 tw:align-top"
                        >
                          {isEditing ? (
                            <Input
                              aria-label={`${column.label} for ${label}`}
                              placeholder={placeholder}
                              value={draft[column.key] ?? ''}
                              onChange={(event) =>
                                setDraft((state) => ({
                                  ...state,
                                  [column.key]: event.target.value,
                                }))
                              }
                            />
                          ) : (
                            <span className="tw:block tw:px-2 tw:py-1 tw:break-words">
                              {shown || placeholder || '—'}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="tw:px-1 tw:py-1 tw:align-top">
                      <div className="tw:flex tw:justify-end tw:gap-1">
                        {isEditing ? (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Cancel editing ${label}`}
                              onClick={() => setEditingIndex(null)}
                            >
                              <X className="tw:size-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Save ${label}`}
                              onClick={() => commitEdit(index)}
                            >
                              <Check className="tw:size-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${label}`}
                            onClick={() => beginEdit(index)}
                          >
                            <Pencil className="tw:size-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </NewCgFormCard>
  );
};

export default IcsCg5136Card;
